import styled, { css, keyframes } from 'styled-components';

import colors from '../../../../../styles/colors';

const { base, shades } = colors;

// Layout-only palette shortcuts. Anything that maps to a reusable component
// (buttons, inputs, chips, switch) is intentionally NOT styled here — those use
// MUI / the shared component library instead.
const LIME = base.primary;
const LIME_B = shades.primary2;
const INK = base.secondary2;
const MINT_D = shades.secondary6;
const SEC_TITLE = shades.secondary3;
const BAR = shades.secondary5;
const BORDER = shades.secondary7;
const XLIGHT = shades.secondary10;
const SAGE = shades.primary1;
const GRAY = shades.gamma10;
const GRAY_BORDER = shades.gamma3;
const DANGER_BRIGHT = shades.danger3;
const DANGER_BG = shades.danger2;
const DANGER_BORDER = shades.danger4;
const SUCCESS = shades.success6;
const SUCCESS_B = shades.success7;
const SUCCESS_BG = '#EAFBE9';
const SUCCESS_BORDER = '#BFE6C6';
const PROGRESS = base.secondary;
const AMBER = base.warning;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: none; }
`;

/* ---------- page ---------- */
const Container = styled.div`
  width: 100%;
  padding-bottom: 96px;
`;

const PageTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 12px 20px 4px;
`;

const PageTitle = styled.div`
  font-size: 22px;
  font-weight: 500;
`;

const CountChip = styled.span`
  background: ${BAR};
  border: 1px solid ${BORDER};
  color: ${SEC_TITLE};
  font-size: 13px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
`;

const Intro = styled.p`
  font-size: 14px;
  color: ${GRAY};
  margin: 0 20px 16px;
  max-width: 720px;
  line-height: 1.5;
`;

const Body = styled.div`
  padding: 0 20px;
`;

/* ---------- drop zone ---------- */
const DropWrap = styled.div`
  position: sticky;
  top: 0;
  z-index: 12;
  background: ${base.alpha};
  padding: 6px 0 14px;
`;

const DropZoneBox = styled.div`
  border: 2px dashed ${MINT_D};
  border-radius: 12px;
  background: ${XLIGHT};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  transition: border-color 0.15s, background 0.15s, padding 0.18s;

  ${({ $big }) => ($big
    ? css`
        flex-direction: column;
        padding: 44px 24px;
        text-align: center;
        gap: 14px;
      `
    : css`
        padding: 16px 22px;
      `)}

  &:hover { border-color: ${SEC_TITLE}; background: ${BAR}; }

  ${({ $dragging }) => $dragging && css`
    border-color: ${SEC_TITLE};
    background: ${BAR};
  `}
`;

const DzIcon = styled.div`
  width: ${({ $big }) => ($big ? '58px' : '44px')};
  height: ${({ $big }) => ($big ? '58px' : '44px')};
  border-radius: 50%;
  background: ${BAR};
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  svg {
    width: ${({ $big }) => ($big ? '28px' : '22px')};
    height: ${({ $big }) => ($big ? '28px' : '22px')};
  }
`;

const DzText = styled.div`
  text-align: ${({ $big }) => ($big ? 'center' : 'left')};
`;

const DzTitle = styled.div`
  font-size: ${({ $big }) => ($big ? '18px' : '15px')};
  font-weight: bold;
  color: ${INK};
`;

const DzSub = styled.div`
  font-size: 13px;
  color: ${GRAY};
  margin-top: 2px;
`;

const DzCta = styled.span`
  background: ${LIME};
  border: 1px solid ${LIME_B};
  color: ${INK};
  font-weight: bold;
  font-size: 14px;
  padding: 10px 18px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: none;

  svg { width: 17px; height: 17px; }
`;

const DzSpacer = styled.span`
  flex: 1;
`;

const DzGuide = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  justify-content: center;
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
`;

const DzGuideItem = styled.li`
  font-size: 12px;
  color: ${GRAY};
  position: relative;
  padding-left: 14px;

  &::before {
    content: '';
    position: absolute;
    left: 2px;
    top: 6px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${MINT_D};
  }
`;

const EmptyNote = styled.div`
  text-align: center;
  color: ${GRAY};
  font-size: 14px;
  padding: 34px 10px;
`;

/* ---------- card shell ---------- */
const Card = styled.div`
  border: 1px solid ${BORDER};
  border-radius: 10px;
  background: ${base.alpha};
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(20, 34, 47, 0.03);

  & + & { margin-top: 14px; }

  ${({ $entering }) => $entering && css`animation: ${cardIn} 0.34s ease both;`}
  ${({ $error }) => $error && css`border-color: ${DANGER_BORDER};`}
  ${({ $saveFailed }) => $saveFailed && css`
    border-color: ${DANGER_BORDER};
    box-shadow: 0 0 0 1px ${DANGER_BORDER};
  `}
  ${({ $saved }) => $saved && css`
    border-color: ${SUCCESS_BORDER};
    background: ${SUCCESS_BG};
  `}
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 16px;
  border-bottom: 1px solid ${BORDER};
  background: ${BAR};
`;

const Chevron = styled.span`
  display: inline-flex;

  svg {
    width: 18px;
    height: 18px;
    transition: transform 0.2s ease;
    transform: rotate(${({ $collapsed }) => ($collapsed ? '-90deg' : '0deg')});
  }
`;

const HeadNum = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${SEC_TITLE};
  color: ${base.alpha};
  font-size: 12px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
`;

const HeadTitle = styled.span`
  font-size: 15px;
  font-weight: bold;
  color: ${INK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ $muted }) => $muted && css`color: ${GRAY}; font-weight: 500;`}
`;

const Grow = styled.span`
  flex: 1;
`;

const CardBody = styled.div`
  display: flex;
  padding: 16px;
  gap: 20px;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const VideoCol = styled.div`
  width: 220px;
  flex: none;

  @media (max-width: 720px) {
    width: 100%;
  }
`;

const FieldsCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  color: ${SAGE};
  margin-bottom: 6px;
  display: block;
  letter-spacing: 0.02em;
`;

// Matches the single-exercise editor's TagContainer.
const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

/* ---------- video box ---------- */
const VideoBox = styled.div`
  border: 1px solid ${BORDER};
  border-radius: 8px;
  aspect-ratio: 1 / 1;
  width: 100%;
  position: relative;
  overflow: hidden;
  background: ${BAR};
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $failed }) => $failed && css`
    border-color: ${DANGER_BORDER};
    background: ${DANGER_BG};
  `}
`;

const VideoOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(20, 34, 47, 0.62);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: ${base.alpha};
  padding: 0 18px;
`;

const Ring = styled.span`
  width: 34px;
  height: 34px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: ${base.alpha};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const OverlayPct = styled.span`
  font-size: 13px;
  font-weight: 600;
`;

const VideoFail = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  color: ${({ $muted }) => ($muted ? GRAY : DANGER_BRIGHT)};
  text-align: center;
  padding: 0 14px;

  svg { width: 30px; height: 30px; }
`;

const VideoFailText = styled.span`
  font-size: 12px;
  font-weight: 600;
`;

const ProgressBar = styled.div`
  margin-top: 11px;
  width: 100%;
  height: 6px;
  border-radius: 4px;
  background: ${BORDER};
  overflow: hidden;

  > i {
    display: block;
    height: 100%;
    background: ${PROGRESS};
    border-radius: 4px;
    width: ${({ $progress }) => `${$progress}%`};
    transition: width 0.3s ease;
  }
`;

const VideoStat = styled.div`
  margin-top: 9px;
  font-size: 12px;
  color: ${({ $ok }) => ($ok ? SUCCESS_B : GRAY)};
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.4;

  svg { width: 13px; height: 13px; flex: none; }
`;

const VideoError = styled.div`
  margin-top: 9px;
  font-size: 12px;
  color: ${DANGER_BRIGHT};
  line-height: 1.5;
`;

const VideoControls = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

/* ---------- saved done-row ---------- */
const SavedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 13px 16px;
`;

const SavedCheck = styled.span`
  width: 27px;
  height: 27px;
  border-radius: 50%;
  background: ${SUCCESS};
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  svg { width: 15px; height: 15px; color: ${base.alpha}; }
`;

const SavedNum = styled.span`
  font-size: 12px;
  font-weight: bold;
  color: ${SUCCESS_B};
  opacity: 0.7;
`;

const SavedName = styled.span`
  font-size: 15px;
  font-weight: bold;
  color: ${INK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SavedNote = styled.span`
  font-size: 12px;
  color: ${SUCCESS_B};
  opacity: 0.85;
  white-space: nowrap;

  @media (max-width: 720px) { display: none; }
`;

/* ---------- save-failed banner ---------- */
const SaveFailBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${DANGER_BG};
  border-top: 1px solid ${DANGER_BORDER};
  padding: 11px 16px;
  font-size: 13px;
  color: ${DANGER_BRIGHT};

  svg { width: 17px; height: 17px; flex: none; }
`;

/* ---------- fixed footer ---------- */
const FootBar = styled.div`
  position: sticky;
  bottom: 0;
  background: ${base.alpha};
  border-top: 1px solid ${BORDER};
  padding: 13px 20px;
  z-index: 20;
  box-shadow: 0 -6px 24px rgba(20, 34, 47, 0.07);
  margin-top: 16px;
`;

const FootInner = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const Summary = styled.div`
  flex: 1;
  font-size: 14px;
  color: ${INK};
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  b { font-weight: bold; }

  .sep { color: ${GRAY_BORDER}; margin: 0 9px; }
  .s-saved { color: ${SUCCESS_B}; font-weight: 700; }
  .s-ready { color: ${SUCCESS_B}; font-weight: 600; }
  .s-up { color: ${PROGRESS}; font-weight: 600; }
  .s-need { color: ${AMBER}; font-weight: 600; }
  .s-fail { color: ${DANGER_BRIGHT}; font-weight: 600; }
  .s-muted { color: ${GRAY}; }
`;

export {
  Container,
  PageTitleRow,
  PageTitle,
  CountChip,
  Intro,
  Body,
  DropWrap,
  DropZoneBox,
  DzIcon,
  DzText,
  DzTitle,
  DzSub,
  DzCta,
  DzSpacer,
  DzGuide,
  DzGuideItem,
  EmptyNote,
  Card,
  CardHead,
  Chevron,
  HeadNum,
  HeadTitle,
  Grow,
  CardBody,
  VideoCol,
  FieldsCol,
  FieldLabel,
  TagList,
  VideoBox,
  VideoOverlay,
  Ring,
  OverlayPct,
  VideoFail,
  VideoFailText,
  ProgressBar,
  VideoStat,
  VideoError,
  VideoControls,
  SavedRow,
  SavedCheck,
  SavedNum,
  SavedName,
  SavedNote,
  SaveFailBar,
  FootBar,
  FootInner,
  Summary,
};
